import { createRoot } from 'solid-js'
import { createStore, unwrap } from 'solid-js/store'
import { EngineInstance } from '../../../engine_wrapper'
import type { GameObject3DFrameEventFunction } from '../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate'
import createDefaultStateConfig from '../state/create_default_state_config'
import createDefaultStateMap from '../state/create_default_state_map'
import { createNewRNGConfig } from '../rng'
import { StateMapDisk } from '../state/state_map_disk.enum'
import { StateType } from '../state/state_type.enum'
import type { State } from '../state/State.type'
import createBlankPlayer from '../player/createBlankPlayer'
import { colorNamesPool } from '../player/colorNamesPool'
import { Engine } from './Engine.type'
import { decodeStateSnapshotUnchecked, encodeStateMessage } from './engineWasmCodec'
import {
  appendMapEditorPlayerToState,
  createEngineWasmManager,
  resetEngineWasmSession,
  type EngineWasmSession,
} from './engineWasm'
import { setStateFromGenerated } from './setEngineState'

let lastEngineId: number = 0

function getPreferredLocalDisk(disk: StateMapDisk): StateMapDisk.IndexedDB | StateMapDisk.Memory | undefined {
  if (disk === StateMapDisk.IndexedDB || disk === StateMapDisk.Memory) {
    return disk
  }
}

async function bootstrapRootStateFromWasm(
  engine: Engine,
  disk: StateMapDisk,
  initialStateSignature: string,
  getCurrentStateSignature: () => string
): Promise<EngineWasmSession> {
  const resetCounter = ++engine.wasm.resetCounter
  const sessionPromise = EngineInstance.create()
    .then((instance) => {
      const snapshot = instance.getBlankStateForEngineInit()
      const nextState = decodeStateSnapshotUnchecked(snapshot, getPreferredLocalDisk(disk))
      nextState.map.disk = disk
      return appendMapEditorPlayerToState(nextState).then((stateWithPlayer) => ({
        handle: instance.createGameFromState(encodeStateMessage(stateWithPlayer)),
        instance,
        nextState: stateWithPlayer,
      }))
    })
    .then(({ handle, instance, nextState }) => {
      if (engine.wasm.resetCounter !== resetCounter) {
        return engine.wasm.sessionPromise as Promise<EngineWasmSession>
      }

      if (getCurrentStateSignature() !== initialStateSignature) {
        engine.wasm.sessionPromise = null
        return resetEngineWasmSession(engine)
      }

      engine.__setDerivedState(nextState)
      const session = { handle, instance }
      engine.wasm.lastResetError = null
      engine.wasm.sessionPromise = Promise.resolve(session)
      return session
    })
    .catch((error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error))
      engine.wasm.lastResetError = err
      throw err
    })

  engine.wasm.sessionPromise = sessionPromise
  return sessionPromise
}

export default function createEngineRoot(disk: StateMapDisk): Engine {
  return createRoot((dispose) => {
    const gcELL: Array<() => void> = [dispose]
    const ofELL: Array<GameObject3DFrameEventFunction> = []
    const initialState: State = {
      round: 1,
      type: StateType.Map,
      config: createDefaultStateConfig(),
      map: createDefaultStateMap(disk),
      width: 1,
      height: 1,
      ents: [],
      players: [createBlankPlayer(1, colorNamesPool[0])],
      turnPlayerId: null,
      rng: createNewRNGConfig(),
      online: null,
    }
    const initialStateSignature = JSON.stringify(initialState)
    const [state, setState] = createStore(initialState)

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const setDerivedState: typeof setState = (...args: any[]) => {
      if (engine) engine.wasm.sessionPromise = null
      return (setState as any)(...args)
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const engine = {
      id: ++lastEngineId,
      wasm: createEngineWasmManager(),
      __setDerivedState: setDerivedState,
      bootstrapWasmState(nextState) {
        return setStateFromGenerated(engine, nextState)
      },
      ofELL() {
        return ofELL
      },
      gcELL() {
        return gcELL
      },
    } as Engine

    engine.bootstrapPromise = bootstrapRootStateFromWasm(
      engine,
      disk,
      initialStateSignature,
      () => JSON.stringify(unwrap(state))
    )

    Object.defineProperty(engine, 'state', {
      configurable: true,
      enumerable: true,
      get() {
        return state
      },
      set() {
        throw new Error('non-flux-state-write')
      },
    })
    return engine
  })
}
